/**
 * Sinh doan ma tuong ung voi request VUA CHAY o /playground.
 *
 * ============================================================================
 *  VI SAO SINH MA THAY VI VIET SAN MOT VI DU
 *
 *  Truoc trang nay, toan bo huong dan API trong dashboard la MOT doan curl viet tay o
 *  man hinh tao key — va no da lac hau: chi co `ImageToTextTask` (mot trong nam loai),
 *  dung `<base64>` lam cho trong nen dan vao la loi, va khong he nhac toi API native du
 *  do la duong gon hon han.
 *
 *  Do la so phan cua moi tai lieu viet tay: no dung vao ngay duoc viet, roi lech dan.
 *  Doan ma o day duoc dung tu CHINH request vua gui di, nen no khong the lech — neu no
 *  sai thi ket qua ben canh no cung sai, va nguoi dung thay ngay.
 * ============================================================================
 */

export type CodeLang = 'curl' | 'ts' | 'python';

export interface CodegenInput {
  baseUrl: string;
  apiKey: string | null;
  /** Body da gui len /v1/solve, nguyen van. */
  body: Record<string, unknown>;
  /** false = thay key that bang mot cho trong. */
  revealKey: boolean;
}

const KEY_PLACEHOLDER = '<API_KEY_CỦA_BẠN>';

function keyOf(input: CodegenInput): string {
  if (!input.revealKey || input.apiKey === null || input.apiKey.length === 0) {
    return KEY_PLACEHOLDER;
  }
  return input.apiKey;
}

/**
 * Anh base64 trong body co the dai vai tram nghin ky tu.
 *
 * Dan nguyen vao mot doan vi du thi khong ai doc duoc, va cai nguoi doc can biet la
 * TEN TRUONG chu khong phai byte cua chinh buc anh ho vua keo tha. Thay bang mot cho
 * trong CO CHU THICH — noi ro do la anh cua ban, dai bao nhieu.
 */
function summarise(body: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(body)) {
    if (k === 'image' && typeof v === 'string') {
      out[k] = `<ảnh base64, ${v.length.toLocaleString('vi-VN')} ký tự>`;
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** JSON nhieu dong, thut le 2 — de doc trong khoi ma. */
function pretty(value: unknown, indent = 2): string {
  return JSON.stringify(value, null, indent);
}

function curl(input: CodegenInput): string {
  const body = pretty(summarise(input.body));
  // Nhay don quanh body de shell khong dien giai `$` hay `"` ben trong.
  return `curl -s ${input.baseUrl}/v1/solve \\
  -H 'Authorization: Bearer ${keyOf(input)}' \\
  -H 'Content-Type: application/json' \\
  -d '${body}'`;
}

function typescript(input: CodegenInput): string {
  const { type, ...rest } = input.body as { type: string } & Record<string, unknown>;
  const args = pretty(summarise({ type, ...rest }));
  return `import { CapForge } from '@capforge/sdk';

const cf = new CapForge({
  baseUrl: '${input.baseUrl}',
  apiKey: '${keyOf(input)}',
});

const r = await cf.solve(${args});

// { ok: false } là KẾT QUẢ, không phải lỗi — nên không cần try/catch quanh đây.
// Chỉ lỗi mạng / hết giờ mới ném.
if (r.ok) {
  console.log('đáp án:', r.answer, '| độ tin cậy:', r.confidence);
} else {
  console.log('không giải được:', r.reason);
}`;
}

function python(input: CodegenInput): string {
  // Python doc JSON gan giong het, chi khac ba tu khoa.
  const body = pretty(summarise(input.body))
    .replace(/\btrue\b/g, 'True')
    .replace(/\bfalse\b/g, 'False')
    .replace(/\bnull\b/g, 'None');
  return `import requests

r = requests.post(
    "${input.baseUrl}/v1/solve",
    headers={"Authorization": "Bearer ${keyOf(input)}"},
    json=${body},
    timeout=60,
).json()

if r["ok"]:
    print("đáp án:", r["answer"], "| độ tin cậy:", r["confidence"])
else:
    print("không giải được:", r["reason"])`;
}

export function generateCode(lang: CodeLang, input: CodegenInput): string {
  if (lang === 'curl') return curl(input);
  if (lang === 'ts') return typescript(input);
  return python(input);
}

export const CODE_LANGS: ReadonlyArray<{ id: CodeLang; label: string }> = [
  { id: 'curl', label: 'cURL' },
  { id: 'ts', label: 'TypeScript (SDK)' },
  { id: 'python', label: 'Python' },
];
